import { z } from 'zod';
/**
 * Canonical Pack Manifest schema. This file is the single source of truth
 * for the pack manifest contract shared by:
 *   - the iOS app and keyboard extension (via quicktype-generated Swift)
 *   - the Cloudflare Worker (Public_Read_API and Admin_Write_API)
 *   - the admin dashboard (client-side validation)
 *
 * Renaming any property here (e.g. switching to snake_case) breaks
 * cross-language canonical JSON equality (Property 3) and the on-disk
 * SHA-256 integrity model (Requirements 16.1, 16.2). Don't do it.
 */
export const PackKind = z.enum(['sticker', 'gif']);
export const PackState = z.enum(['draft', 'published', 'unpublished']);
export const PackAsset = z.object({
    id: z.string().regex(/^[a-z0-9-]{4,64}$/),
    fileName: z.string().min(1).max(128),
    url: z.string().regex(/^\/v1\/assets\/[a-z0-9-]+\/[a-z0-9-]+$/),
    byteSize: z
        .number()
        .int()
        .min(1)
        .max(8 * 1024 * 1024),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    durationMs: z.number().int().positive().optional(),
});
export const PackManifest = z.object({
    id: z.string().regex(/^[a-z0-9-]{2,64}$/),
    kind: PackKind,
    name: z.string().min(1).max(64),
    description: z.string().max(512).optional(),
    version: z.number().int().min(1),
    publishedAt: z.string().datetime(),
    coverAssetId: z.string().optional(),
    assets: z.array(PackAsset).min(0).max(200),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
export const CatalogEntry = z.object({
    id: z.string(),
    kind: PackKind,
    name: z.string(),
    version: z.number().int().min(1),
    manifestUrl: z.string(),
    coverUrl: z.string().optional(),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    publishedAt: z.string().datetime(),
});
export const CatalogManifest = z.object({
    generatedAt: z.string().datetime(),
    packs: z.array(CatalogEntry),
});
/**
 * Client-side metadata form schema for the Admin_Dashboard pack editor.
 *
 * Mirrors the human-editable subset of the canonical {@link PackManifest}
 * (Requirement 22.1: name, description, kind, tags). Cover image is
 * controlled separately on the editor by clicking "Set as cover" on a
 * pack asset (the Worker stores `cover_asset_id`, not a free URL).
 */
export const PackMetadataInput = z.object({
    name: z.string().trim().min(1, 'Tên pack không được để trống').max(80, 'Tên pack tối đa 80 ký tự'),
    description: z
        .string()
        .max(500, 'Mô tả tối đa 500 ký tự')
        .optional()
        .default(''),
    kind: PackKind,
    priceVnd: z
        .number({ invalid_type_error: 'Giá phải là số nguyên không âm' })
        .int('Giá phải là số nguyên không âm')
        .min(0, 'Giá phải >= 0')
        .max(999999999, 'Giá quá lớn')
        .optional()
        .default(0),
    tags: z
        .array(z.string().trim().min(1).max(32))
        .max(10, 'Tối đa 10 thẻ')
        .optional()
        .default([]),
});
/**
 * Typed error thrown by `parsePackManifest` and `parseCatalogManifest`.
 * Wraps the underlying ZodError so callers can either render the
 * human-readable message or inspect `cause.issues` for structured
 * field-level diagnostics.
 */
export class ManifestParseError extends Error {
    name = 'ManifestParseError';
    cause;
    constructor(target, cause) {
        const summary = cause.issues
            .slice(0, 5)
            .map((issue) => `${issue.path.join('.') || '<root>'}: ${issue.message}`)
            .join('; ');
        super(`Invalid ${target}: ${summary}`);
        this.cause = cause;
    }
}
export function parsePackManifest(value) {
    const result = PackManifest.safeParse(value);
    if (!result.success) {
        throw new ManifestParseError('PackManifest', result.error);
    }
    return result.data;
}
export function parseCatalogManifest(value) {
    const result = CatalogManifest.safeParse(value);
    if (!result.success) {
        throw new ManifestParseError('CatalogManifest', result.error);
    }
    return result.data;
}
//# sourceMappingURL=schema.js.map